<script lang="ts" setup>
import { computed } from 'vue';

const props = defineProps({
  collapsable: { type: Boolean, default: false },
  horizontal: { type: Boolean, default: false },
  isCollapsed: { type: Boolean, default: false },
  resizable: { type: Boolean, default: false },
  reverseIcon: { type: Boolean, default: false },
});
const emit = defineEmits<{
  (e: 'button-click'): void;
  (e: 'button-mousedown'): void;
}>();

const showArrowUp = computed<boolean>(
  () =>
    (!props.reverseIcon && ((props.resizable && !props.isCollapsed) || (!props.resizable && !props.isCollapsed))) ||
    (props.reverseIcon && (props.resizable || (!props.resizable && props.isCollapsed))),
);
const showArrowDown = computed<boolean>(
  () =>
    (!props.reverseIcon && (props.resizable || (!props.resizable && props.isCollapsed))) ||
    (props.reverseIcon && ((props.resizable && !props.isCollapsed) || (!props.resizable && !props.isCollapsed))),
);
</script>

<template>
  <button :class="['smart-layout-button', { 'smart-layout-button--resizable': resizable, 'smart-layout-button--horizontal': horizontal }]">
    <div
      class="smart-layout-button__actions"
      @mousedown="resizable && emit('button-mousedown')"
      @click="emit('button-click')"
    >
      <div class="smart-layout-button__icons">
        <ot-icon
          v-if="showArrowUp"
          class="smart-layout-button__icon"
          name="arrow-up"
          inherit-stroke="inherit-stroke"
        />
        <ot-icon
          v-if="showArrowDown"
          class="smart-layout-button__icon"
          name="arrow-down"
          inherit-stroke="inherit-stroke"
        />
      </div>
    </div>
  </button>
</template>

<style lang="scss">
.smart-layout-button {
  --layout-control-button-width: 3px;
  --layout-control-button-height: 100%;
  --layout-control-actions-width: 3px;
  --layout-control-actions-height: 80px;
  --actions-background-color: rgb(var(--v-theme-elevation-1));
  --icons-background-color: rgb(var(--v-theme-elevation-2));

  position: relative;
  z-index: var(--collapse-button-ot-z-index);
  display: flex;
  width: var(--layout-control-button-width);
  height: var(--layout-control-button-height);
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: rgb(var(--v-theme-elevation-0));
  color: rgb(var(--v-theme-text-tertiary));
  cursor: default;

  &:hover {
    z-index: calc(var(--collapse-button-ot-z-index) + 1);

    --actions-background-color: rgb(var(--v-theme-elevation-1));
  }

  &__actions {
    display: flex;
    width: var(--layout-control-actions-width);
    height: var(--layout-control-actions-height);
    align-items: center;
    justify-content: center;
    border: 1px solid rgb(var(--v-theme-elevation-2));
    border-radius: 12px;
    background-color: var(--actions-background-color);
    cursor: pointer;
    outline: 1px solid rgb(var(--v-theme-elevation-0));
    transition: 0.3s background-color;

    &:hover {
      --icons-background-color: rgb(var(--v-theme-primary));
      --actions-background-color: rgb(var(--v-theme-primary));

      color: rgb(var(--v-theme-text-primary));
    }
  }

  &__icons {
    z-index: 1;
    display: flex;
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: center;
    padding: 1px;
    border-radius: 50%;
    background-color: var(--icons-background-color);
    transition: 0.3s;

    path {
      stroke-width: 3px;
    }

    .smart-layout-button:hover & {
      width: 20px;
      height: 20px;
    }
  }

  &__icon {
    z-index: var(--collapse-button-z-index);
    display: flex;
    overflow: visible;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transform: rotate(90deg);
  }
}

.smart-layout-button--horizontal {
  --layout-control-button-width: 100%;
  --layout-control-button-height: 3px;
  --layout-control-actions-width: 80px;
  --layout-control-actions-height: 3px;

  .smart-layout-button__icon {
    transform: rotate(0deg);
  }
}

.smart-layout-button--resizable {
  z-index: var(--resizable-drag-handle-z-index);

  .smart-layout-button__icon {
    width: 90%;
  }
}
</style>
